import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useCreateRecruitmentContext } from '../../../../../../Create/Contexts/CreateRecruitmentContext';
import { DemoQuestions } from '../../../../../../Models/Questions';
import type { SelectTestsFormValues } from '../../../../SelectTestsStep.schema';
import AptitudeTestCard from './Components/AptitudeTestCard/AptitudeTestCard';
import EmotionalIntelligenceTestCard from './Components/EmotionalIntelligenceTestCard/EmotionalIntelligenceTestCard';
import PersonalityTestCard from './Components/PersonalityTestCard/PersonalityTestCard';
import SkillsTestCard from './Components/SkillsTestCard/SkillsTestCard';
import TestCardGroup from './Components/TestCardGroup/TestCardGroup';
import { TestTypes } from '@/Models/Configuration';
import UIStrings from '@/Utils/UIStrings';

function TestCards() {
    const { testInfos, getDemoQuestions } = useCreateRecruitmentContext();
    const { testLanguageField } = useWatch<SelectTestsFormValues>();

    const [demoQuestions, setDemoQuestions] = useState<DemoQuestions | undefined>();
    const findTest = useCallback((type: TestTypes) => testInfos.find((item) => item.testType === type), [testInfos]);
    const [personality, skills, aptitude, emotionalIntelligence] = useMemo(
        () => [
            findTest(TestTypes.Personality),
            findTest(TestTypes.Skills),
            findTest(TestTypes.Aptitude),
            findTest(TestTypes.EmotionalIntelligence),
        ],
        [findTest],
    );

    useEffect(() => {
        const getAndSetDemoQuestions = async () => {
            const result = await getDemoQuestions(testLanguageField!);
            setDemoQuestions(result);
        };

        setDemoQuestions(undefined);
        getAndSetDemoQuestions();
    }, [testLanguageField, setDemoQuestions]);

    return (
        <div className="grid justify-center gap-x-4">
            <TestCardGroup groupName={UIStrings.HardSkills}>
                {skills && (
                    <SkillsTestCard
                        test={skills}
                        demoQuestions={demoQuestions?.skills}
                        likertOptions={demoQuestions?.likertAnswers}
                    />
                )}
                {aptitude && (
                    <AptitudeTestCard
                        test={aptitude}
                        demoQuestions={demoQuestions?.aptitude}
                        likertOptions={demoQuestions?.likertAnswers}
                    />
                )}
            </TestCardGroup>
            <TestCardGroup groupName={UIStrings.SoftSkills}>
                {personality && (
                    <PersonalityTestCard
                        test={personality}
                        demoQuestions={demoQuestions?.personality}
                        likertOptions={demoQuestions?.likertAnswers}
                    />
                )}
                {emotionalIntelligence && (
                    <EmotionalIntelligenceTestCard
                        test={emotionalIntelligence}
                        demoQuestions={demoQuestions?.emotionalIntelligence}
                        likertOptions={demoQuestions?.likertAnswers}
                    />
                )}
            </TestCardGroup>
        </div>
    );
}

export default TestCards;
